import React, { useState, useEffect } from "react";
import { IAppState } from "redux/reducers";
import { connect } from "react-redux";
import { accountService } from "api/services/account-service";
import {
  AccountType,
  accountTypeDict,
  AuthenticationMode,
  ssoProviderDictionary,
} from "api/entities/account";
import {
  MicrosoftAuthButton,
  MicrosoftIcon,
} from "components/account-actions/external-auth/buttons/microsoft-auth-button";
import { SettingsTab } from "components/shared-ui/tabs/tabs";
import Button from "components/shared-ui/button";
import { useApi } from "utils/hooks/use-api";
import { ChangeSetPasswordPanel } from "./panels/change-set-password-panel";
import styles from "./account-tab.module.scss";
import { RecoveryCodesDialog } from "./dialogs/recovery-codes-dialog";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { commonActions } from "redux/actions/common";
import { usersThunkActions } from "redux/actions/users";
import { MfaPanel } from "./panels/mfa-panel";
import { IRegisterMFADeviceResponse } from "api/entities/mfa";
import { Label } from "components/shared-ui/label";

const mapStateToProps = (state: IAppState) => ({
  user: state.users.currentUser,
  loginFailed: state.account.loginFailed,
  loginError: state.account.loginError,
});

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
  return {
    showSnackBar: (message: string) => {
      return dispatch(commonActions.showSnackBar(true, message));
    },
    getCurrentUser: () => dispatch(usersThunkActions.getCurrentUser()),
  };
}

interface IAccountTabProps
  extends
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  activeTab: SettingsTab;
}

export const AccountTab = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: IAccountTabProps) => {
  const [api] = useApi();
  const [authenticationMode, setAuthenticationMode] =
    useState<AuthenticationMode>(props.user.authenticationMode);
  const [hasExternalAccount, setHasExternalAccount] = useState<boolean>(
    !!props.user.externalAccount,
  );
  const [changeSetPasswordPanelOpen, setChangeSetPasswordPanelOpen] =
    useState<boolean>(false);
  const [manageMfa, setManageMfa] = useState<boolean>(false);
  const [recoveryCodes, setRecoveryCodes] =
    useState<IRegisterMFADeviceResponse[]>();
  const [isRecoveryCodesDialogOpen, setRecoveryCodesDialogOpen] =
    useState<boolean>();

  function getExternalAccountSection() {
    return (
      <section>
        <div className={styles.row}>
          <div>SSO:</div>
          <div>
            {!hasExternalAccount
              ? "None"
              : ssoProviderDictionary[props.user.externalAccount.ssoProvider]}
          </div>
        </div>

        <div className={styles["column-button"]}>
          {!hasExternalAccount ? (
            <MicrosoftAuthButton
              redirectUri={window.location.href}
              buttonText={"Attach Microsoft Account"}
            />
          ) : (
            <Button
              colors="primary"
              onClick={() => {
                api(accountService.detachExternalAccount()).then(() =>
                  setHasExternalAccount(false),
                );
              }}
              testId="button-sign-up"
            >
              <MicrosoftIcon />
              Detach Account
            </Button>
          )}
        </div>
      </section>
    );
  }

  const changeSetPasswordButtonText =
    authenticationMode === AuthenticationMode.SSOOnly
      ? "Set Password"
      : "Change Password";

  return (
    <>
      <section>
        <Label label="user" value={props.user.name} />
        <Label label="email" value={props.user.email} />
        <Label label="type" value={accountTypeDict[props.user.accountType]} />
        {props.user.accountType === AccountType.Enterprise && (
          <Label label="organization name" value={props.user.organization} />
        )}
      </section>
      {/* <section>
        {authenticationMode === AuthenticationMode.SSOOnly && (
          <div className={styles["authentication-mode-desc"]}>
          Your account is directly logged in from Single-Sign On
          </div>
          )}
          </section> */}
      <Label
        label="actions"
        value={
          <div className={styles.actions}>
            <Button
              colors="primary"
              onClick={() => setChangeSetPasswordPanelOpen(true)}
              testId="button-sign-up"
            >
              {changeSetPasswordButtonText}
            </Button>
            <Button
              colors="primary"
              onClick={() => setManageMfa(true)}
              testId="button-manage-mfa"
            >
              Manage MFA
            </Button>
          </div>
        }
      />

      {/* {getExternalAccountSection()} */}

      <ChangeSetPasswordPanel
        open={changeSetPasswordPanelOpen}
        onClose={(newAuthenticationMode: AuthenticationMode) => {
          setChangeSetPasswordPanelOpen(false);
          setAuthenticationMode(newAuthenticationMode);
        }}
      />

      <MfaPanel
        open={manageMfa}
        onClose={(recoveryCodes) => {
          if (recoveryCodes?.length > 0) {
            setRecoveryCodes(recoveryCodes);
            setRecoveryCodesDialogOpen(true);
          }

          setManageMfa(false);
          props.getCurrentUser();
        }}
      />
      <RecoveryCodesDialog
        open={isRecoveryCodesDialogOpen}
        onClose={() => setRecoveryCodesDialogOpen(false)}
        recoveryCodes={recoveryCodes}
      />
    </>
  );
});
